import React from 'react'

interface CheckboxProps {
    label?: React.ReactNode
    checked: boolean // Checked state
    className?: string
    id?: string // Unique ID for the checkbox
    onChange: (checked: boolean) => void // Change handler
    disabled?: boolean // Disabled state
    size?: 'small' | 'medium' | 'large' // Define the size of the checkbox
    color?: 'primary' | 'success' | 'warning' | 'secondary' // Define the color of the checkbox
}

const Checkbox: React.FC<CheckboxProps> = ({
    label,
    checked,
    id,
    onChange,
    className = '',
    disabled = false,
    size = 'medium', // Default size is medium
    color = 'primary' // Default color is primary
}) => {
    const sizeClass = {
        small: 'w-3 h-3',
        medium: 'w-4 h-4',
        large: 'w-5 h-5'
    }[size]

    const colorClass = {
        primary: 'text-brand-500 border-brand-300',
        success: 'text-green-500 border-green-300',
        warning: 'text-yellow-500 border-yellow-300',
        secondary: 'text-violet-500 border-violet-300'
    }[color]

    // Define the selected and unselected state colors
    const checkedClass = {
        primary: 'bg-brand-500 border-blue-500',
        success: 'bg-green-500 border-green-500',
        warning: 'bg-yellow-500 border-yellow-500',
        secondary: 'bg-violet-500 border-violet-500'
    }[color]

    const uncheckedClass = {
        primary: 'bg-white border-gray-300 dark:bg-gray-700 dark:border-brand-600',
        success: 'bg-white border-gray-300 dark:bg-gray-700 dark:border-green-600',
        warning: 'bg-white border-gray-300 dark:bg-gray-700 dark:border-yellow-600',
        secondary: 'bg-white border-gray-300 dark:bg-gray-700 dark:border-violet-600'
    }[color]

    return (
        <label
            className={`flex items-center space-x-3 cursor-pointer ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
        >
            <input
                id={id}
                type='checkbox'
                className={`rounded dark:focus:ring-0 focus:ring-0 focus:outline-none dark:focus:bg-outline-none ${sizeClass} ${className} 
                ${checked ? checkedClass : uncheckedClass} ${colorClass}`}
                checked={checked}
                onChange={e => onChange(e.target.checked)}
                disabled={disabled}
            />
            {label && <span className='font-medium text-gray-800 text-theme-sm dark:text-white'>{label}</span>}
        </label>
    )
}

export default Checkbox
